import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, {
  TileStatusContent,
} from "../../../components/Tile/TileContent";
import { useLocalFilters } from "../../../hooks/useLocalFilters";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { getOCELMetadata } from "../../../services/apiClient";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import AttributeTableSettings from "./AttributeTableSettings";
import LocalFiltersActiveBadge from "../../../components/common/LocalFiltersActiveBadge";
import AttributeValueBlock from "./AttributeValueBlock";

const AttributeTable = ({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) => {
  const { t } = useTranslation();
  // ✅ Use local filters hook - automatically selects local or global filters
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    api,
    currentFilters,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  // State management
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);
  const [pendingSelectionClear, setPendingSelectionClear] = useState(false);
  const [objectAttributes, setObjectAttributes] = useState([]);

  // Object Table configuration
  const [mainAttribute, setMainAttribute] = useState("");
  const [additionalAttributes, setAdditionalAttributes] = useState([]);
  const [componentTitle, setComponentTitle] = useState("");
  const [hideEmptyValues, setHideEmptyValues] = useState(false);

  // Always-current mirror of mainAttribute for the async metadata-fetch effect
  // below, which otherwise reads a stale closure captured before the
  // mount-restore effect (initialSettings) has applied the saved value.
  const mainAttributeRef = useRef(mainAttribute);
  mainAttributeRef.current = mainAttribute;

  // Sorting state
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ROWS_PER_PAGE = 10;

  // Legacy support - keep for backward compatibility
  const [selectedAttribute, setSelectedAttribute] = useState("");

  // Edge/Node mode state
  const [filterMode, setFilterMode] = useState(null); // null, 'edge', or 'node'
  const [selectedObjectIds, setSelectedObjectIds] = useState([]);
  const [selectionLabel, setSelectionLabel] = useState(""); // e.g., "Activity1 → Activity2" or "Activity Name"

  // Modal selection mode state
  const [selectionMode, setSelectionMode] = useState(null); // null | 'edge' | 'node' - controls if component is actively waiting for selection
  const [selectedEdgeInfo, setSelectedEdgeInfo] = useState(null); // Stores selected edge/node details

  // Settings modal state
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Waiting for edge selection to populate a local edge filter
  const [waitingForEdgeFilter, setWaitingForEdgeFilter] = useState(null); // null | 'duration' | 'containment'


  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.mainAttribute !== undefined) setMainAttribute(initialSettings.mainAttribute);
    if (initialSettings.additionalAttributes !== undefined) setAdditionalAttributes(initialSettings.additionalAttributes);
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    if (initialSettings.hideEmptyValues !== undefined) setHideEmptyValues(initialSettings.hideEmptyValues);
    setPendingApply(true);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({ mainAttribute, additionalAttributes, componentTitle, localFilters, hideEmptyValues }));
  }, [mainAttribute, additionalAttributes, componentTitle, localFilters, hideEmptyValues]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sync the tile title to the parent header — also fires after the
  // mount-restore effect applies a saved componentTitle.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(componentTitle || "Attribute Table");
  }, [componentTitle]);

  const handleOpenSettings = useCallback(() => {
    startDraftMode(); // Enter draft mode when opening modal
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const handleSettingsOpenChange = useCallback((val) => {
    setIsSettingsModalOpen(val);
    if (!val) cancelDraftFilters(); // Discard draft on close (no-op when Apply committed it)
  }, [cancelDraftFilters]);

  const handleActivateEdgeDurationFilter = useCallback(() => {
    cancelDraftFilters();
    setIsSettingsModalOpen(false);
    setWaitingForEdgeFilter('duration');
  }, [cancelDraftFilters]);

  const handleActivateEdgeContainmentFilter = useCallback(() => {
    cancelDraftFilters();
    setIsSettingsModalOpen(false);
    setWaitingForEdgeFilter('containment');
  }, [cancelDraftFilters]);

  // Fetch object attributes from metadata (EXCLUDE event attributes)
  useEffect(() => {
    const fetchAttributes = async () => {
      if (!activeFile) return;

      try {
        const metadata = await getOCELMetadata(activeFile);
        console.log("📊 AttributeTable - Fetched metadata:", metadata);

        // IMPORTANT: Use object_attribute_names, NOT event_attribute_names
        const objAttrs = metadata?.object_attribute_names || [];
        setObjectAttributes(objAttrs);

        // Auto-select first attribute as main attribute if none selected.
        // Checked via ref (not the closed-over `mainAttribute`) since a saved
        // config may have restored it after this effect started but before
        // this async fetch resolved.
        if (!mainAttributeRef.current && objAttrs.length > 0) {
          setMainAttribute(objAttrs[0]);
          setSelectedAttribute(objAttrs[0]); // For backward compatibility
          console.log(`✅ Auto-selected first main attribute: ${objAttrs[0]}`);
        }
      } catch (err) {
        console.error("❌ Failed to fetch object attributes:", err);
        setObjectAttributes([]);
      }
    };

    fetchAttributes();
  }, [activeFile]);

  // Event listeners for Edge/Node mode (MODAL: only respond when mode is active)
  useEffect(() => {
    const handleEdgeSelected = (event) => {
      // Edge Duration Filter: add a default duration filter for the clicked edge
      if (waitingForEdgeFilter === "duration") {
        const { edgeLabel, sourceActivity, targetActivity } = event.detail;
        const newFilter = {
          edgeId: `${sourceActivity}→${targetActivity}`,
          edgeLabel: edgeLabel || `${sourceActivity} → ${targetActivity}`,
          sourceActivity,
          targetActivity,
          operator: "le",
          value: 5,
          unit: "days",
          mode: "include",
          scopeKind: "total_objects",
          thresholdMs: 432000000,
        };
        const updatedFilters = [...(localFilters.edgeDurationFilters || []), newFilter];
        updateLocalFilter("edgeDurationFilters", updatedFilters);
        setWaitingForEdgeFilter(null);
        setHasFetched(true);
        return;
      }

      // Edge Containment Filter: add the clicked edge to containment filter
      if (waitingForEdgeFilter === "containment") {
        const { edgeLabel, sourceActivity, targetActivity } = event.detail;
        const newEdge = {
          sourceActivity,
          targetActivity,
          edgeLabel: edgeLabel || `${sourceActivity} → ${targetActivity}`,
        };
        const current = localFilters.edgeContainmentFilters || {
          enabled: true,
          edges: [],
          matchType: "any",
          mode: "include",
        };
        updateLocalFilter("edgeContainmentFilters", {
          ...current,
          enabled: true,
          edges: [...(current.edges || []), newEdge],
        });
        setWaitingForEdgeFilter(null);
        setHasFetched(true);
        return;
      }

      // Only process selection mode if active (existing "Select Edge/Node" flow)
      if (selectionMode !== "active") {
        console.log("⏭️  Ignoring edge click - selection mode not active");
        return;
      }

      const { edgeLabel, objectIds, objectType } = event.detail;
      console.log("📊 Edge selected in active mode:", {
        edgeLabel,
        objectType,
        objectCount: objectIds?.length || 0,
      });

      // Store selection details
      setSelectedEdgeInfo({
        label: edgeLabel,
        objectIds,
        objectType,
      });
      setFilterMode("edge");
      setSelectedObjectIds(objectIds || []);
      setSelectionLabel(edgeLabel || "Selected Edge");
      setSelectionMode(null); // Deactivate mode after selection (one-shot)
      setHasFetched(true);
    };

    const handleNodeSelected = (event) => {
      // Only process if selection mode is active
      if (selectionMode !== "active") {
        console.log("⏭️  Ignoring node click - selection mode not active");
        return;
      }

      const { nodeLabel, objectIds } = event.detail;
      console.log("📊 Node selected in active mode:", {
        nodeLabel,
        objectCount: objectIds?.length || 0,
      });

      // Store selection details
      setSelectedEdgeInfo({
        label: nodeLabel,
        objectIds,
      });
      setFilterMode("node");
      setSelectedObjectIds(objectIds || []);
      setSelectionLabel(nodeLabel || "Selected Node");
      setSelectionMode(null); // Deactivate mode after selection (one-shot)
      setHasFetched(true);
    };

    window.addEventListener(
      "attribute-table:edge-selected",
      handleEdgeSelected,
    );
    window.addEventListener(
      "attribute-table:node-selected",
      handleNodeSelected,
    );

    return () => {
      window.removeEventListener(
        "attribute-table:edge-selected",
        handleEdgeSelected,
      );
      window.removeEventListener(
        "attribute-table:node-selected",
        handleNodeSelected,
      );
    };
  }, [selectionMode, waitingForEdgeFilter, localFilters, updateLocalFilter]); // Re-create when any of these change

  // Fetch table data
  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a filter change) must not overwrite a
  // newer one.
  const fetchSeqRef = useRef(0);
  const fetchData = async () => {
    if (!mainAttribute) {
      console.warn("⚠️ Cannot fetch data: No main attribute selected");
      return;
    }

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      // Build request payload for Object Table
      const requestPayload = {
        main_attribute: mainAttribute,
        additional_attributes:
          additionalAttributes.length > 0 ? additionalAttributes : null,
      };

      // Include object_ids when in Edge/Node mode
      if (filterMode && selectedObjectIds.length > 0) {
        requestPayload.object_ids = selectedObjectIds;
        console.log(
          `📤 Fetching Object Table: main='${mainAttribute}', ` +
            `additional=[${additionalAttributes.join(", ")}], ` +
            `(${filterMode} mode: ${selectedObjectIds.length} objects)`,
        );
      } else {
        console.log(
          `📤 Fetching Object Table: main='${mainAttribute}', ` +
            `additional=[${additionalAttributes.join(", ")}]`,
        );
      }

      // useGlobalAPI automatically applies global filters
      const response = await api.post(
        "/ocel/object-attribute-table",
        requestPayload,
      );

      console.log(`✅ Received Object Table data:`, response);
      console.log(`📊 Data details:`, {
        hasRows: !!response?.rows,
        rowCount: response?.rows?.length || 0,
        mainAttribute: response?.main_attribute,
        additionalAttributes: response?.additional_attributes,
        totalObjects: response?.total_objects || 0,
        totalNull: response?.total_null || 0,
      });

      if (seq !== fetchSeqRef.current) return;
      setTableData(response);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error("❌ Object Table Fetch Error:", err);
      setError(err.message || "An unknown error occurred.");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  };

  // Auto-reload on filter/file changes
  useEffect(() => {
    if (hasFetched && mainAttribute) {
      console.log("🔄 Auto-reload triggered (filters or file changed)");
      fetchData();
    }
  }, [currentFilters, activeFile]);

  // Auto-reload on Edge/Node mode changes
  useEffect(() => {
    if (
      hasFetched &&
      mainAttribute &&
      filterMode &&
      selectedObjectIds.length > 0
    ) {
      console.log(
        `🔄 Auto-reload triggered (${filterMode} mode: ${selectedObjectIds.length} objects)`,
      );
      fetchData();
    }
  }, [filterMode, selectedObjectIds]);

  // Handle column sort
  const handleColumnSort = useCallback(
    (columnName) => {
      if (sortBy === columnName) {
        // Toggle sort order
        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        // Sort by new column (ascending by default)
        setSortBy(columnName);
        setSortOrder("asc");
      }
      setCurrentPage(1);
    },
    [sortBy],
  );

  // Rows to display, optionally hiding empty main-attribute values and
  // recomputing percentages relative to only the visible (non-empty) rows
  const visibleRows = useMemo(() => {
    if (!tableData || !tableData.rows) return [];
    if (!hideEmptyValues) return tableData.rows;

    const filtered = tableData.rows.filter(
      (row) => row.value !== null && row.value !== undefined,
    );
    const total = tableData.total_with_value ?? 0;
    return filtered.map((row) => ({
      ...row,
      percentage: total > 0 ? (row.count / total) * 100 : 0,
    }));
  }, [tableData, hideEmptyValues]);

  // Frontend sorting - sort rows without making API calls
  const sortedRows = useMemo(() => {
    if (!visibleRows || visibleRows.length === 0) {
      return [];
    }

    const rows = [...visibleRows];
    const mainAttr = tableData.main_attribute || tableData.attribute_name;
    const columnToSort = sortBy || mainAttr;

    rows.sort((a, b) => {
      let aVal, bVal;

      // Determine which column we're sorting by
      if (columnToSort === "count") {
        aVal = a.count;
        bVal = b.count;
      } else if (columnToSort === "percentage") {
        aVal = a.percentage;
        bVal = b.percentage;
      } else if (columnToSort === mainAttr) {
        aVal = a.value;
        bVal = b.value;
      } else if (
        tableData.additional_attributes &&
        tableData.additional_attributes.includes(columnToSort)
      ) {
        aVal = a.additional_values?.[columnToSort];
        bVal = b.additional_values?.[columnToSort];
      } else {
        return 0;
      }

      // Handle null/undefined values - always sort them to the end
      const aIsNull = aVal === null || aVal === undefined;
      const bIsNull = bVal === null || bVal === undefined;

      if (aIsNull && bIsNull) return 0;
      if (aIsNull) return 1;
      if (bIsNull) return -1;

      // Determine if values are numeric
      const aIsNumber =
        typeof aVal === "number" ||
        (!isNaN(parseFloat(aVal)) && isFinite(aVal));
      const bIsNumber =
        typeof bVal === "number" ||
        (!isNaN(parseFloat(bVal)) && isFinite(bVal));

      if (aIsNumber && bIsNumber) {
        const aNum = typeof aVal === "number" ? aVal : parseFloat(aVal);
        const bNum = typeof bVal === "number" ? bVal : parseFloat(bVal);
        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      }

      if (aIsNumber && !bIsNumber) return sortOrder === "asc" ? -1 : 1;
      if (!aIsNumber && bIsNumber) return sortOrder === "asc" ? 1 : -1;

      const aStr = String(aVal);
      const bStr = String(bVal);
      const comparison = aStr.localeCompare(bStr);
      return sortOrder === "asc" ? comparison : -comparison;
    });

    return rows;
  }, [tableData, visibleRows, sortBy, sortOrder]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedRows.length / ROWS_PER_PAGE);
  const indexOfLastRow = currentPage * ROWS_PER_PAGE;
  const indexOfFirstRow = indexOfLastRow - ROWS_PER_PAGE;

  const paginatedRows = useMemo(() => {
    return sortedRows.slice(indexOfFirstRow, indexOfLastRow);
  }, [sortedRows, indexOfFirstRow, indexOfLastRow]);

  const getPageGroup = useCallback(() => {
    const groupStart = Math.floor((currentPage - 1) / 5) * 5 + 1;
    const groupEnd = Math.min(groupStart + 4, totalPages);
    return Array.from(
      { length: groupEnd - groupStart + 1 },
      (_, i) => groupStart + i,
    );
  }, [currentPage, totalPages]);


  // Header actions - settings button and local filters badge
  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {hasLocalFilters && (
          <LocalFiltersActiveBadge
            hasLocalFilters={hasLocalFilters}
            localFilterCount={localFilterCount}
            onClearLocalFilters={clearLocalFilters}
          />
        )}
        <button
          onClick={handleOpenSettings}
          className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={t('common:actions.openSettings')}
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="text-xs">{t('common:actions.settings')}</span>
        </button>
      </div>
    ),
    [handleOpenSettings, hasLocalFilters, localFilterCount, clearLocalFilters],
  );

  // Apply handler - commit draft filters and signal pending fetch
  const handleApply = useCallback(() => {
    applyDraftFilters();
    setPendingApply(true);
    if (onTitleChange && componentTitle) {
      onTitleChange(componentTitle);
    }
  }, [applyDraftFilters, onTitleChange, componentTitle]);

  // Fire fetch after all state updates from handleApply have landed
  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    fetchData();
  }, [pendingApply, fetchData]);

  // Fire fetch after selection-clear state updates have landed
  useEffect(() => {
    if (!pendingSelectionClear) return;
    setPendingSelectionClear(false);
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSelectionClear]);

  const handleApplySettings = useCallback(() => {
    handleApply();
  }, [handleApply]);

  // Auto-load data when persistent cache is enabled
  useAutoLoad(fetchData, [activeFile, currentFilters], tableData);

  // Register refresh function with parent (GridComponent)
  useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchData);
    }
  }, [onRefresh, fetchData]);

  // Register header actions with parent (GridComponent)
  useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(headerActions);
    }
  }, [onGetHeaderActions, headerActions]);

  // Force parent re-render when the local-filter badge appears or disappears.
  // The effect above handles general headerActions changes, but the parent sometimes
  // needs an extra nudge when hasLocalFilters toggles (same workaround as AggregationKPI).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLocalFilters]);

  // Determine what to show
  const hasData =
    !loading &&
    !error &&
    tableData &&
    tableData.rows &&
    tableData.rows.length > 0;
  const showInitialHint = !hasFetched && !loading;

  return (
    <BaseTile
      title={componentTitle || "Attribute Table"}
      loading={loading}
      minHeight="auto"
      padding="none"
    >
      <TileContent layout="flex" direction="column" grow>
        {/* Active edge filter indicators (always visible when filters are set) */}
        {!waitingForEdgeFilter && (localFilters.edgeDurationFilters?.length > 0 || (localFilters.edgeContainmentFilters?.enabled && localFilters.edgeContainmentFilters?.edges?.length > 0)) && (
          <div className="mx-3 mt-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md flex flex-wrap gap-2 items-center text-xs">
            {localFilters.edgeDurationFilters?.length > 0 && (
              <>
                <span className="font-medium text-gray-600">Duration filters:</span>
                {localFilters.edgeDurationFilters.map((f, i) => (
                  <span key={i} className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded font-medium">
                    {f.edgeLabel} {f.operator === "ge" ? ">=" : "<="} {f.value} {f.unit}
                  </span>
                ))}
              </>
            )}
            {localFilters.edgeContainmentFilters?.enabled && localFilters.edgeContainmentFilters?.edges?.length > 0 && (
              <>
                <span className="font-medium text-gray-600 ml-1">Containment:</span>
                {localFilters.edgeContainmentFilters.edges.map((e, i) => (
                  <span key={i} className="px-2 py-0.5 bg-green-100 text-green-800 rounded font-medium">
                    {e.edgeLabel || `${e.sourceActivity} → ${e.targetActivity}`}
                  </span>
                ))}
              </>
            )}
          </div>
        )}

        {/* Waiting for edge selection indicator */}
        {waitingForEdgeFilter && (
          <div className="mx-3 mt-2 p-3 bg-blue-50 border-2 border-blue-300 rounded-md animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
                </svg>
                <p className="text-sm font-semibold text-blue-800">
                  Click an edge on the graph to add{" "}
                  {waitingForEdgeFilter === "duration" ? "Edge Duration" : "Edge Containment"} filter
                </p>
              </div>
              <button
                onClick={() => setWaitingForEdgeFilter(null)}
                className="px-3 py-1 text-xs bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-100 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Initial Hint - when no data fetched yet */}
        {showInitialHint && (
          <TileStatusContent
            status="info"
            title="Configure Attribute Table"
            message="Click the gear icon above to select a main attribute and configure columns."
            icon={
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            }
          />
        )}

        {/* Loading State */}
        {loading && <TileStatusContent status="loading" />}

        {/* Error State */}
        {!loading && error && (
          <TileStatusContent
            status="error"
            title="Failed to load attribute table"
            message={error}
            action={
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Retry
              </button>
            }
          />
        )}

        {/* Table Display */}
        {hasData && (
          <div className="overflow-auto flex-grow">
            {/* Persistent Header showing current attribute and selection */}
            <div className="sticky top-0 z-20 bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-300 px-4 py-3 shadow-sm">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                      />
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">
                      Main Attribute:
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                      {tableData.main_attribute || tableData.attribute_name}
                    </span>
                  </div>

                  {tableData.additional_attributes &&
                    tableData.additional_attributes.length > 0 && (
                      <>
                        <div className="h-4 w-px bg-gray-300"></div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-700">
                            Additional:
                          </span>
                          {tableData.additional_attributes.map((attr, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium"
                            >
                              {attr}
                            </span>
                          ))}
                        </div>
                      </>
                    )}

                  {filterMode && selectedEdgeInfo && (
                    <>
                      <div className="h-4 w-px bg-gray-300"></div>
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-purple-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                          />
                        </svg>
                        <span className="text-sm font-semibold text-gray-700">
                          {filterMode === "edge" ? "Edge:" : "Node:"}
                        </span>
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm font-medium">
                          {selectionLabel}
                        </span>
                        <span className="text-xs text-gray-600">
                          ({selectedObjectIds.length}{" "}
                          {selectedObjectIds.length === 1
                            ? "object"
                            : "objects"}
                          )
                        </span>
                      </div>
                    </>
                  )}

                  {localFilters.edgeDurationFilters?.length > 0 && (
                    <>
                      <div className="h-4 w-px bg-gray-300"></div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-700">Duration:</span>
                        {localFilters.edgeDurationFilters.map((f, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium"
                          >
                            {f.edgeLabel} {f.operator === "ge" ? ">=" : "<="} {f.value} {f.unit}
                          </span>
                        ))}
                      </div>
                    </>
                  )}

                  {localFilters.edgeContainmentFilters?.enabled &&
                    localFilters.edgeContainmentFilters?.edges?.length > 0 && (
                    <>
                      <div className="h-4 w-px bg-gray-300"></div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-700">Containment:</span>
                        {localFilters.edgeContainmentFilters.edges.map((e, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium"
                          >
                            {e.edgeLabel || `${e.sourceActivity} → ${e.targetActivity}`}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {filterMode && (
                  <button
                    onClick={() => {
                      setFilterMode(null);
                      setSelectedObjectIds([]);
                      setSelectionLabel("");
                      setSelectedEdgeInfo(null);
                      window.dispatchEvent(new CustomEvent("attribute-table:selection-cleared"));
                      console.log("🔄 Cleared selection from table header");
                      if (hasFetched && mainAttribute) setPendingSelectionClear(true);
                    }}
                    className="px-3 py-1 text-xs bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-100 transition-colors font-medium flex items-center gap-1"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Clear
                  </button>
                )}
              </div>
            </div>

            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {/* Main Attribute Column Header with Sorting */}
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() =>
                      handleColumnSort(
                        tableData.main_attribute || tableData.attribute_name,
                      )
                    }
                  >
                    <div className="flex items-center gap-1">
                      <span>
                        {tableData.main_attribute || tableData.attribute_name}
                      </span>
                      {sortBy ===
                        (tableData.main_attribute ||
                          tableData.attribute_name) && (
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          {sortOrder === "asc" ? (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          ) : (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          )}
                        </svg>
                      )}
                    </div>
                  </th>

                  {/* Additional Attribute Column Headers with Sorting */}
                  {tableData.additional_attributes &&
                    tableData.additional_attributes.map((attr) => (
                      <th
                        key={attr}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleColumnSort(attr)}
                      >
                        <div className="flex items-center gap-1">
                          <span>{attr}</span>
                          {sortBy === attr && (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              {sortOrder === "asc" ? (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 15l7-7 7 7"
                                />
                              ) : (
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 9l-7 7-7-7"
                                />
                              )}
                            </svg>
                          )}
                        </div>
                      </th>
                    ))}

                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleColumnSort("count")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span>Count</span>
                      {sortBy === "count" && (
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          {sortOrder === "asc" ? (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          ) : (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          )}
                        </svg>
                      )}
                    </div>
                  </th>

                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleColumnSort("percentage")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      <span>Percentage</span>
                      {sortBy === "percentage" && (
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          {sortOrder === "asc" ? (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          ) : (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          )}
                        </svg>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRows.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    {/* Main Attribute Value */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {row.value !== null && row.value !== undefined ? (
                        row.value
                      ) : (
                        <span className="text-gray-400 italic">(empty)</span>
                      )}
                    </td>

                    {/* Additional Attribute Values */}
                    {tableData.additional_attributes &&
                      tableData.additional_attributes.map((attr) => (
                        <td
                          key={attr}
                          className="px-0 py-0"
                          style={{ minWidth: "200px", maxWidth: "300px" }}
                        >
                          <div
                            className="overflow-x-auto px-6 py-4"
                            style={{ scrollbarWidth: "thin", scrollbarColor: "#cbd5e1 #f1f5f9" }}
                          >
                            {row.additional_values &&
                            row.additional_values[attr] !== null &&
                            row.additional_values[attr] !== undefined ? (
                              <div
                                className="flex gap-2 py-1"
                                style={{ minWidth: "max-content", whiteSpace: "nowrap" }}
                              >
                                {String(row.additional_values[attr])
                                  .split(", ")
                                  .map((value, valueIdx) => (
                                    <AttributeValueBlock
                                      key={`${attr}-${idx}-${valueIdx}`}
                                      value={value}
                                      index={valueIdx}
                                    />
                                  ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic text-sm">
                                (empty)
                              </span>
                            )}
                          </div>
                        </td>
                      ))}

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                      {row.count.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {row.percentage.toFixed(1)}%
                    </td>
                  </tr>
                ))}

              </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center px-6 py-4 bg-white border-t border-gray-200 relative">
                <div className="absolute left-6 text-sm text-gray-600">
                  Showing {indexOfFirstRow + 1}-
                  {Math.min(indexOfLastRow, sortedRows.length)} of{" "}
                  {sortedRows.length} rows
                </div>
                <div className="flex items-center space-x-1">
                  {/* First Page */}
                  {currentPage > 2 && (
                    <>
                      <button
                        onClick={() => setCurrentPage(1)}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
                        title="First page"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                          />
                        </svg>
                      </button>
                      {getPageGroup()[0] > 2 && (
                        <span className="px-2 py-2 text-gray-400">...</span>
                      )}
                    </>
                  )}

                  {/* Previous Page */}
                  {currentPage > 1 && (
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
                      title="Previous page"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                  )}

                  {/* Page Numbers */}
                  {getPageGroup().map((num) => (
                    <button
                      key={num}
                      onClick={() => setCurrentPage(num)}
                      className={`inline-flex items-center px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-md min-w-[2.5rem] justify-center ${
                        num === currentPage
                          ? "bg-blue-600 text-white border border-blue-600 shadow-sm"
                          : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                      }`}
                    >
                      {num}
                    </button>
                  ))}

                  {/* Next Page */}
                  {currentPage < totalPages && (
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
                      title="Next page"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  )}

                  {/* Last Page */}
                  {currentPage < totalPages - 1 && (
                    <>
                      {getPageGroup()[getPageGroup().length - 1] <
                        totalPages - 1 && (
                        <span className="px-2 py-2 text-gray-400">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors duration-200"
                        title="Last page"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 5l7 7-7 7m-8-14l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Summary Footer */}
            <div className="px-6 py-3 bg-gray-50 border-t text-sm text-gray-600 sticky bottom-0">
              <div className="flex justify-between items-center">
                <span className="font-medium">
                  Total:{" "}
                  {(hideEmptyValues
                    ? tableData.total_with_value ?? tableData.total_objects
                    : tableData.total_objects
                  ).toLocaleString()}{" "}
                  objects
                </span>
                {tableData.total_null > 0 && (
                  <span className="text-gray-500">
                    {tableData.total_null.toLocaleString()}{" "}
                    {hideEmptyValues ? "hidden (empty values)" : "with empty values"}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* No Data Message */}
        {hasFetched && !loading && !error && !hasData && (
          <TileStatusContent
            status="empty"
            title="No Data Available"
            message="No attribute values found for the selected filters."
            action={
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Retry
              </button>
            }
          />
        )}
      </TileContent>

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={handleSettingsOpenChange}
        onApply={handleApplySettings}
        title="Attribute Table Settings"
        isApplyDisabled={!mainAttribute || loading}
        applyButtonText={loading ? "Loading..." : "Apply"}
      >
        <AttributeTableSettings
          mainAttribute={mainAttribute}
          onMainAttributeChange={setMainAttribute}
          additionalAttributes={additionalAttributes}
          onAdditionalAttributesChange={setAdditionalAttributes}
          componentTitle={componentTitle}
          onTitleChange={setComponentTitle}
          hideEmptyValues={hideEmptyValues}
          onHideEmptyValuesChange={setHideEmptyValues}
          objectAttributes={objectAttributes}
          loading={loading}
          selectionMode={selectionMode}
          onActivateSelectionMode={() => {
            setSelectionMode("active");
            setIsSettingsModalOpen(false);
            console.log("🎯 Edge/Node selection mode activated");
          }}
          onCancelSelectionMode={() => {
            setSelectionMode(null);
            console.log("❌ Selection mode canceled");
          }}
          filterMode={filterMode}
          selectionLabel={selectionLabel}
          selectedObjectIds={selectedObjectIds}
          onClearSelection={() => {
            setFilterMode(null);
            setSelectedObjectIds([]);
            setSelectionLabel("");
            setSelectedEdgeInfo(null);
            window.dispatchEvent(new CustomEvent("attribute-table:selection-cleared"));
            console.log("🔄 Edge/Node mode reset - clearing selection");
            if (hasFetched && mainAttribute) setPendingSelectionClear(true);
          }}
          selectedEdgeInfo={selectedEdgeInfo}
          localFilters={localFilters}
          updateLocalFilter={updateLocalFilter}
          activeFile={activeFile}
          onAddEdgeDurationFilter={handleActivateEdgeDurationFilter}
          onAddEdgeContainmentFilter={handleActivateEdgeContainmentFilter}
        />
      </TileSettingsModal>
    </BaseTile>
  );
};

export default AttributeTable;
